// react
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// components
import { Badge } from '@interstate/components/Badge';
import { StyledCreditDecisionBadgeButton } from './CreditAppDecision.styles';

// actions
import * as fullLenderDecisionsActionCreators from '@makemydeal/dr-dash-store/actions/fullLenderDecisionsActionCreators';
import * as navigationActionCreators from '@makemydeal/dr-dash-store/actions/navigationActionCreators';

// selectors
import * as creditAppSelectors from '@makemydeal/dr-dash-store/selectors/creditAppSelectors';
import * as fullLenderDecisionsSelectors from '@makemydeal/dr-dash-store/selectors/fullLenderDecisionsSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';

// constants and shared
import { CASH } from '@makemydeal/dr-platform-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import {
    CASH_ORDER_ATTACHED_BADGE_LABEL,
    CASH_ORDER_COMPLETED_BADGE_LABEL,
    CREDIT_APP_ATTACHED_BADGE_LABEL,
    CREDIT_APP_COMPLETED_BADGE_LABEL,
    DECISION_AVAILABLE_BADGE_LABEL
} from '../constants';

export const CreditDecisionStatusBadge = () => {
    const isLenderDecisionFromFSEnabled = useSelector(featureToggleSelectors.isEnableMVLenderDecisions);
    const isClearerSearchAndAttachExperienceEnabled = useSelector(featureToggleSelectors.isClearerSearchAndAttachExperienceEnabled);
    const isFldFinanceMethodSameAsCurrentOfferType = useSelector(
        fullLenderDecisionsSelectors.isFldFinanceMethodSameAsCurrentOfferType
    );
    const hasLenderDecisions = useSelector(fullLenderDecisionsSelectors.getHasFullLenderDecisions);
    const isLoadingLenderDecisions = useSelector(fullLenderDecisionsSelectors.getIsLoadingFullLenderDecisions);
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const creditAppId = useSelector(creditAppSelectors.getCreditAppReferenceId);
    const dispatch = useDispatch();
    const [creditAppBadgeLabel, setCreditAppBadgeLabel] = useState<string>('');
    const [isGoingToNavigate, setIsGoingToNavigate] = useState(false);
    const offerTypeRef = useRef<string>(offerType);
    const creditAppIdRef = useRef<string | null>(creditAppId);

    const isFinanceMethodMatch = isLenderDecisionFromFSEnabled && isFldFinanceMethodSameAsCurrentOfferType;
    const shouldNotRenderBadge = !creditAppBadgeLabel;

    const handleNavigateToCreditDecision = () => {
        dispatch(navigationActionCreators.navigateToCreditDecision());
        setIsGoingToNavigate(false);
    };

    const handleIsGoingToNavigate = () => {
        if (!isFinanceMethodMatch || isGoingToNavigate) {
            return;
        }
        setIsGoingToNavigate(true);
        dispatch(fullLenderDecisionsActionCreators.fetchLenderDecisions());
    };

    useEffect(() => {
        const hasChangedOfferType = offerTypeRef.current !== offerType;
        const isSameCreditAppId = creditAppIdRef.current === creditAppId;

        let badgeLabel = '';
        if (hasLenderDecisions && isFinanceMethodMatch) {
            badgeLabel = DECISION_AVAILABLE_BADGE_LABEL;
        } else if (hasChangedOfferType && isSameCreditAppId && creditAppBadgeLabel !== DECISION_AVAILABLE_BADGE_LABEL) {
            badgeLabel = ''; // Reset badge label if offer type changes
        } else if (creditAppId) {
            const cashOrderLabel = isClearerSearchAndAttachExperienceEnabled
                ? CASH_ORDER_ATTACHED_BADGE_LABEL
                : CASH_ORDER_COMPLETED_BADGE_LABEL;
            const creditAppLabel = isClearerSearchAndAttachExperienceEnabled
                ? CREDIT_APP_ATTACHED_BADGE_LABEL
                : CREDIT_APP_COMPLETED_BADGE_LABEL;
            badgeLabel = offerType === CASH ? cashOrderLabel : creditAppLabel;
        }
        setCreditAppBadgeLabel(badgeLabel);
        creditAppIdRef.current = creditAppId;
    }, [creditAppId, offerType, hasLenderDecisions, isFinanceMethodMatch, isClearerSearchAndAttachExperienceEnabled]);

    useEffect(() => {
        if (!isFinanceMethodMatch) {
            return;
        }

        if (isGoingToNavigate && !isLoadingLenderDecisions) {
            handleNavigateToCreditDecision();
        }
    }, [isGoingToNavigate, isLoadingLenderDecisions, isFinanceMethodMatch]);

    if (shouldNotRenderBadge) {
        return null;
    }

    return (
        <Badge data-testid="credit-app" variant="transparent" sx={{ marginRight: '0.7rem' }}>
            <StyledCreditDecisionBadgeButton
                type="button"
                onClick={handleIsGoingToNavigate}
                $isFinanceMethodMatch={isFinanceMethodMatch}
            >
                {creditAppBadgeLabel}
            </StyledCreditDecisionBadgeButton>
        </Badge>
    );
};
